import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { Helmet } from "react-helmet-async";
import {
  ArrowRight,
  CheckCircle2,
  Clock,
  Code2,
  ExternalLink,
  FolderKanban,
  Globe2,
  Layers3,
  Rocket,
  ShieldCheck,
  Sparkles,
  UserRound,
} from "lucide-react";

const API = import.meta.env.VITE_API_URL || "https://goawin.xyz";

function cleanText(text = "") {
  return String(text).replaceAll('"', "").trim();
}

function safeUrl(url = "") {
  if (!url) return "#";
  if (url.startsWith("http")) return url;
  return `https://${url}`;
}

function getPlainTextFromHtml(html = "") {
  return html
    .replace(/<[^>]+>/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function BgEffect() {
  return (
    <>
      <div className="absolute top-0 left-0 w-[500px] h-[500px] bg-primary/10 blur-[140px] rounded-full" />
      <div className="absolute bottom-0 right-0 w-[500px] h-[500px] bg-orange-300/10 blur-[140px] rounded-full" />

      <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(rgba(0,0,0,0.05)_1px,transparent_1px),linear-gradient(90deg,rgba(0,0,0,0.05)_1px,transparent_1px)] dark:bg-[linear-gradient(rgba(255,255,255,0.05)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.05)_1px,transparent_1px)] bg-[size:70px_70px]" />
    </>
  );
}

function CaseStudyDetails() {
  const { slug } = useParams();

  const [caseStudy, setCaseStudy] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchCaseStudy = async () => {
    try {
      setLoading(true);

      const singleRes = await axios.get(
        `${API}/api/domain/case-study/${slug}`,
        {
          withCredentials: true,
        },
      );

      const current =
        singleRes.data?.data ||
        singleRes.data?.caseStudy ||
        singleRes.data?.caseStudies ||
        null;

      setCaseStudy(Array.isArray(current) ? current[0] : current);

      const allRes = await axios.get(`${API}/api/domain/case-studies`, {
        withCredentials: true,
      });

      const allData = allRes.data?.data || allRes.data?.caseStudies || [];

      setRelated(allData.filter((item) => item.slug !== slug).slice(0, 3));
    } catch (err) {
      console.log("Server Error");
      setCaseStudy(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCaseStudy();
    window.scrollTo(0, 0);
  }, [slug]);

  const overviewText = useMemo(() => {
    if (!caseStudy?.content) return "";
    return getPlainTextFromHtml(caseStudy.content).slice(0, 420);
  }, [caseStudy]);

  if (loading) {
    return (
      <main className="min-h-screen flex items-center justify-center bg-[#fafafa] dark:bg-black">
        <BgEffect />
        <div className="relative z-10 text-center">
          <div className="mx-auto h-12 w-12 rounded-full border-4 border-orange-200 dark:border-white/10 border-t-primary animate-spin" />
          <p className="mt-4 text-gray-600 dark:text-gray-400 font-medium">
            Loading case study...
          </p>
        </div>
      </main>
    );
  }

  if (!caseStudy) {
    return (
      <main className="relative min-h-screen flex items-center justify-center px-5 bg-[#fafafa] dark:bg-black">
        <BgEffect />
        <div className="relative z-10 text-center">
          <h1 className="text-4xl font-black text-gray-900 dark:text-white">
            Case Study Not Found
          </h1>

          <Link
            to="/case-studies"
            className="mt-5 inline-flex items-center gap-2 rounded-lg bg-primary px-6 py-3 text-white font-bold"
          >
            Back to Case Studies <ArrowRight size={18} />
          </Link>
        </div>
      </main>
    );
  }

  const metaCards = [
    {
      icon: <UserRound size={18} />,
      title: "Client",
      value: caseStudy.clientName || "Web X Secure",
    },
    {
      icon: <Globe2 size={18} />,
      title: "Industry",
      value: caseStudy.industry || "Digital Business",
    },
    {
      icon: <Clock size={18} />,
      title: "Duration",
      value: caseStudy.projectDuration || "Flexible",
    },
    {
      icon: <Rocket size={18} />,
      title: "Result",
      value: caseStudy.results || "100% Proven",
    },
  ];

  const resultCards = [
    {
      icon: <Rocket />,
      value: caseStudy.results || "100%",
      label: "Project Result",
    },
    {
      icon: <ShieldCheck />,
      value: "Secure",
      label: "Protected System",
    },
    {
      icon: <Layers3 />,
      value: caseStudy.services?.length || 0,
      label: "Services Delivered",
    },
    {
      icon: <Code2 />,
      value: caseStudy.technologies?.length || 0,
      label: "Technologies Used",
    },
  ];

  return (
    <>
      <Helmet>
        <title>{caseStudy?.seo?.metaTitle || caseStudy?.title}</title>

        <meta
          name="description"
          content={
            caseStudy?.seo?.metaDescription || caseStudy?.shortDescription
          }
        />

        <link
          rel="canonical"
          href={`https://webxsecure.in/case-study/${caseStudy.slug}`}
        />

        <meta property="og:title" content={caseStudy.title} />

        <meta
          property="og:description"
          content={
            caseStudy?.seo?.metaDescription || caseStudy?.shortDescription
          }
        />

        <meta
          property="og:image"
          content={`https://goawin.xyz/${caseStudy.featuredImage}`}
        />

        <meta
          property="og:url"
          content={`https://webxsecure.in/case-study/${caseStudy.slug}`}
        />

        <meta property="og:type" content="article" />

        <meta name="twitter:card" content="summary_large_image" />

        <meta
          name="twitter:image"
          content={`https://goawin.xyz/${caseStudy.featuredImage}`}
        />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",

            mainEntityOfPage: {
              "@type": "WebPage",
              "@id": `https://webxsecure.in/case-study/${caseStudy.slug}`,
            },

            headline: caseStudy.title,

            description: caseStudy.shortDescription,

            image: `https://goawin.xyz/${caseStudy.featuredImage}`,

            datePublished: caseStudy.createdAt,

            dateModified: caseStudy.updatedAt,

            author: {
              "@type": "Organization",
              name: "Web X Secure",
            },

            publisher: {
              "@type": "Organization",
              name: "Web X Secure",
              logo: {
                "@type": "ImageObject",
                url: "https://webxsecure.in/logo.png",
              },
            },
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              {
                "@type": "ListItem",
                position: 1,
                name: "Home",
                item: "https://webxsecure.in",
              },
              {
                "@type": "ListItem",
                position: 2,
                name: "Case Studies",
                item: "https://webxsecure.in/case-studies",
              },
              {
                "@type": "ListItem",
                position: 3,
                name: caseStudy.title,
                item: `https://webxsecure.in/case-study/${caseStudy.slug}`,
              },
            ],
          })}
        </script>
      </Helmet>
      <main className="overflow-hidden bg-[#fafafa] text-gray-900 dark:bg-black dark:text-white">
        {/* HERO */}
        <section className="relative overflow-hidden px-5 pt-28 pb-16">
          <BgEffect />

          <div className="relative z-10 mx-auto max-w-7xl">
            <div className="grid items-center gap-12 lg:grid-cols-2">
              <div>
                <h1 className="mt-5 max-w-2xl text-4xl font-black leading-[1.05] md:text-6xl">
                  {caseStudy.title}
                </h1>

                <div className="mt-8 grid grid-cols-2 gap-3 md:grid-cols-4">
                  {metaCards.map((item, index) => (
                    <div
                      key={index}
                      className="rounded-lg border border-black/5 bg-white/80 p-4 shadow-sm backdrop-blur-xl dark:border-white/10 dark:bg-white/5"
                    >
                      <div className="mb-3 flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10 text-primary">
                        {item.icon}
                      </div>

                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {item.title}
                      </p>

                      <h3 className="mt-1 text-sm font-black leading-snug text-gray-900 dark:text-white">
                        {item.value}
                      </h3>
                    </div>
                  ))}
                </div>

                <div className="mt-8 flex flex-wrap gap-3">
                  {caseStudy.projectUrl && (
                    <a
                      href={safeUrl(caseStudy.projectUrl)}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-2 rounded-lg bg-primary px-6 py-3 text-white font-bold shadow-[0_15px_40px_rgba(254,120,83,.25)] hover:scale-105 transition"
                    >
                      Visit Project <ExternalLink size={18} />
                    </a>
                  )}

                  <Link
                    to="/contact"
                    className="inline-flex items-center gap-2 rounded-lg border border-black/5 bg-white px-6 py-3 font-bold text-gray-900 dark:border-white/10 dark:bg-white/10 dark:text-white hover:border-primary/40 transition"
                  >
                    Get Similar Project <ArrowRight size={18} />
                  </Link>
                </div>
              </div>

              <div className="relative overflow-hidden rounded-[28px] border border-black/5 dark:border-white/10 bg-white dark:bg-white/5 shadow-[0_25px_80px_rgba(0,0,0,.12)]">
                <img
                  src={`https://goawin.xyz/${caseStudy.featuredImage}`}
                  alt={caseStudy.title}
                  className="w-full  h-auto object-contain object-top transition duration-700 hover:scale-105"
                />
              </div>
            </div>
          </div>
        </section>

        {/* OVERVIEW + SERVICES */}
        <section className="relative overflow-hidden px-5 py-14">
          <BgEffect />

          <div className="relative z-10 mx-auto grid max-w-7xl gap-6 lg:grid-cols-2">
            <div className="rounded-lg border border-black/5 bg-white/80 p-8 shadow-sm backdrop-blur-xl dark:border-white/10 dark:bg-white/5">
              <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary/10 text-primary">
                <Sparkles size={24} />
              </div>

              <h2 className="mt-5 text-3xl font-black md:text-4xl">
                Project Overview
              </h2>

              <p className="mt-4 leading-relaxed text-gray-600 dark:text-gray-400">
                {overviewText}...
              </p>
            </div>

            <div className="rounded-lg border border-black/5 bg-white/80 p-8 shadow-sm backdrop-blur-xl dark:border-white/10 dark:bg-white/5">
              <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary/10 text-primary">
                <FolderKanban size={24} />
              </div>

              <h2 className="mt-5 text-3xl font-black md:text-4xl">
                Services Delivered
              </h2>

              <div className="mt-6 grid gap-3 sm:grid-cols-2">
                {caseStudy.services?.map((service, index) => (
                  <div
                    key={index}
                    className="flex items-center gap-3 rounded-lg border border-black/5 bg-[#fafafa] p-4 dark:border-white/10 dark:bg-black/30"
                  >
                    <CheckCircle2 size={18} className="text-primary shrink-0" />
                    <span className="text-sm font-bold">
                      {cleanText(service)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* FULL CONTENT */}
        <section className="relative overflow-hidden px-5 py-14">
          <BgEffect />

          <div className="relative z-10 mx-auto max-w-7xl rounded-lg border border-black/5 bg-white/90 p-7 shadow-sm backdrop-blur-xl dark:border-white/10 dark:bg-white/5 md:p-12">
            <div
              className=" jodit-content
            
            "
              dangerouslySetInnerHTML={{ __html: caseStudy.content }}
            />
          </div>
        </section>

        {/* TECHNOLOGIES */}
        <section className="relative overflow-hidden px-5 py-14">
          <BgEffect />

          <div className="relative z-10 mx-auto max-w-7xl rounded-lg border border-black/5 bg-white/80 p-8 shadow-sm backdrop-blur-xl dark:border-white/10 dark:bg-white/5">
            <div className="max-w-2xl">
              <span className="text-sm font-black text-primary">
                Technology Stack
              </span>

              <h2 className="mt-3 text-3xl font-black md:text-5xl">
                Tools & Technologies Used
              </h2>
            </div>

            <div className="mt-10 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
              {caseStudy.technologies?.map((tech, index) => (
                <div
                  key={index}
                  className="rounded-lg border border-black/5 bg-[#fafafa] p-5 text-center transition hover:-translate-y-1 dark:border-white/10 dark:bg-black/30"
                >
                  <Code2 className="mx-auto text-primary" size={26} />
                  <p className="mt-3 text-sm font-black">{cleanText(tech)}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* TEAM + IMPACT */}
        <section className="relative overflow-hidden px-5 py-14">
          <BgEffect />

          <div className="relative z-10 mx-auto grid max-w-7xl gap-6 lg:grid-cols-2">
            {caseStudy.teamMemberId && (
              <div className="rounded-lg border border-black/5 bg-white/80 p-8 shadow-sm backdrop-blur-xl dark:border-white/10 dark:bg-white/5">
                <span className="text-sm font-black text-primary">
                  Project Lead
                </span>

                <h2 className="mt-3 text-3xl font-black md:text-4xl">
                  {caseStudy.teamMemberId.name}
                </h2>

                <p className="mt-2 font-bold text-primary">
                  {caseStudy.teamMemberId.post}
                </p>

                <p className="mt-5 leading-relaxed text-gray-600 dark:text-gray-400">
                  {caseStudy.teamMemberId.description}
                </p>
              </div>
            )}

            <div className="rounded-lg border border-white/10 bg-[#111827] p-8 text-white shadow-[0_25px_80px_rgba(0,0,0,.18)] dark:bg-white/5">
              <span className="text-sm font-black text-primary">
                Business Impact
              </span>

              <h2 className="mt-3 text-3xl font-black md:text-4xl">
                Built for Long-Term Growth
              </h2>

              <p className="mt-5 leading-relaxed text-white/70">
                This project improved digital operations, reduced manual work,
                and created a scalable system for future growth.
              </p>

              <div className="mt-8 grid gap-4 sm:grid-cols-2">
                {[
                  "Faster Workflow",
                  "Better User Experience",
                  "SEO Ready Structure",
                  "Secure Architecture",
                ].map((item, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <CheckCircle2 className="text-primary" size={20} />
                    <span className="text-sm font-semibold">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* RELATED */}
        {related.length > 0 && (
          <section className="relative overflow-hidden px-5 py-14">
            <BgEffect />

            <div className="relative z-10 mx-auto max-w-7xl">
              <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
                <div>
                  <span className="text-sm font-black text-primary">
                    More Work
                  </span>

                  <h2 className="mt-3 text-3xl font-black md:text-5xl">
                    Related Case Studies
                  </h2>
                </div>

                <Link
                  to="/case-studies"
                  className="inline-flex items-center gap-2 font-bold text-primary"
                >
                  View All <ArrowRight size={18} />
                </Link>
              </div>

              <div className="mt-10 grid gap-6 md:grid-cols-3">
                {related.slice(0, 3).map((item) => (
                  <Link
                    key={item._id}
                    to={`/case-study/${item.slug}`}
                    className="group rounded-md border border-black/5 bg-white/80 p-2 shadow-sm backdrop-blur-xl transition hover:-translate-y-2 dark:border-white/10 dark:bg-white/5"
                  >
                    <div className="overflow-hidden rounded-md">
                      <img
                        src={`https://goawin.xyz/${item.featuredImage}`}
                        alt={item.title}
                        className="h-52 w-full object-cover transition "
                      />
                    </div>

                    <h3 className="mt-5 text-xl font-black px-2 text-gray-900 dark:text-white">
                      {item.title}
                    </h3>

                    <p className="mt-2 line-clamp-3 px-2 text-sm leading-relaxed text-gray-500 dark:text-gray-400">
                      {item.shortDescription}
                    </p>

                    <div className="mt-5 px-2 inline-flex items-center gap-2 text-sm font-bold text-primary">
                      View Detailed Case <ArrowRight size={16} />
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
    </>
  );
}

export default CaseStudyDetails;
